/**
 * ============================================================================
 * PASSWORD STRENGTH COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Visual password strength indicator
 */

import React, { useMemo } from 'react';
import { useI18n } from '../../providers';
import ProgressBar from '../common/ProgressBar';

/**
 * Password Strength Component
 * @param {Object} props
 * @param {string} props.password - Password to analyze
 * @param {boolean} props.showRequirements - Show password requirements
 * @returns {JSX.Element}
 */
const PasswordStrength = ({ password = '', showRequirements = true }) => {
  const { language } = useI18n();

  const strength = useMemo(() => {
    if (!password) return { score: 0, label: '', variant: 'default' };

    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    if (checks.length) score += 1;
    if (checks.lowercase) score += 1;
    if (checks.uppercase) score += 1;
    if (checks.number) score += 1;
    if (checks.special) score += 1;

    const percentage = (score / 5) * 100;

    let label, variant;
    if (score <= 1) {
      label = language === 'fr' ? 'Très faible' : 'Very Weak';
      variant = 'error';
    } else if (score === 2) {
      label = language === 'fr' ? 'Faible' : 'Weak';
      variant = 'error';
    } else if (score === 3) {
      label = language === 'fr' ? 'Moyen' : 'Medium';
      variant = 'warning';
    } else if (score === 4) {
      label = language === 'fr' ? 'Fort' : 'Strong';
      variant = 'info';
    } else {
      label = language === 'fr' ? 'Très fort' : 'Very Strong';
      variant = 'success';
    }

    return { score, percentage, label, variant, checks };
  }, [password, language]);

  if (!password && !showRequirements) return null;

  return (
    <div className="space-y-3 animate-fadeInUp">
      {password && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {language === 'fr' ? 'Force du mot de passe' : 'Password Strength'}
            </span>
            <span
              className={`text-sm font-bold ${
                strength.variant === 'error'
                  ? 'text-red-600 dark:text-red-400'
                  : strength.variant === 'warning'
                  ? 'text-orange-600 dark:text-orange-400'
                  : strength.variant === 'info'
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-green-600 dark:text-green-400'
              }`}
            >
              {strength.label}
            </span>
          </div>
          <ProgressBar
            value={strength.percentage}
            variant={strength.variant}
            animated
            showLabel={false}
            className="h-2"
          />
        </div>
      )}

      {showRequirements && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
            {language === 'fr' ? 'Exigences:' : 'Requirements:'}
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-2">
              <span
                className={`${
                  strength.checks?.length ? 'text-green-500' : 'text-gray-400'
                }`}
              >
                {strength.checks?.length ? '✓' : '○'}
              </span>
              <span
                className={
                  strength.checks?.length
                    ? 'text-gray-700 dark:text-gray-300'
                    : 'text-gray-500 dark:text-gray-400'
                }
              >
                {language === 'fr' ? '8+ caractères' : '8+ characters'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`${
                  strength.checks?.lowercase ? 'text-green-500' : 'text-gray-400'
                }`}
              >
                {strength.checks?.lowercase ? '✓' : '○'}
              </span>
              <span
                className={
                  strength.checks?.lowercase
                    ? 'text-gray-700 dark:text-gray-300'
                    : 'text-gray-500 dark:text-gray-400'
                }
              >
                {language === 'fr' ? 'Minuscule' : 'Lowercase'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`${
                  strength.checks?.uppercase ? 'text-green-500' : 'text-gray-400'
                }`}
              >
                {strength.checks?.uppercase ? '✓' : '○'}
              </span>
              <span
                className={
                  strength.checks?.uppercase
                    ? 'text-gray-700 dark:text-gray-300'
                    : 'text-gray-500 dark:text-gray-400'
                }
              >
                {language === 'fr' ? 'Majuscule' : 'Uppercase'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`${
                  strength.checks?.number ? 'text-green-500' : 'text-gray-400'
                }`}
              >
                {strength.checks?.number ? '✓' : '○'}
              </span>
              <span
                className={
                  strength.checks?.number
                    ? 'text-gray-700 dark:text-gray-300'
                    : 'text-gray-500 dark:text-gray-400'
                }
              >
                {language === 'fr' ? 'Chiffre' : 'Number'}
              </span>
            </div>
            <div className="flex items-center gap-2 col-span-2">
              <span
                className={`${
                  strength.checks?.special ? 'text-green-500' : 'text-gray-400'
                }`}
              >
                {strength.checks?.special ? '✓' : '○'}
              </span>
              <span
                className={
                  strength.checks?.special
                    ? 'text-gray-700 dark:text-gray-300'
                    : 'text-gray-500 dark:text-gray-400'
                }
              >
                {language === 'fr' ? 'Caractère spécial' : 'Special character'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordStrength;
